// Import UI components from shared component library
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { TagInput } from '@/components/ui/tag-input';

// Import custom hooks
import { useSettings } from '@/hooks/useSettings';

// Import React hooks
import { useState } from 'react';

// Import feature components
import { ConfirmDialog } from '@/components/feature/confirm-dialog';

interface DeleteTagState {
  field: 'employeeRole' | 'specialCategory' | 'serviceCategory';
  tag: string;
}

// Component for managing roles and categories
const RoleAndCategoryTab = () => {
  const { settings, updateSettings } = useSettings();
  const [deleteTagState, setDeleteTagState] = useState<DeleteTagState | null>(null);

  const handleTagsChange =
    (field: 'employeeRole' | 'specialCategory' | 'serviceCategory') => (tags: string[]) => {
      if (!settings?.id) return;
      updateSettings({
        id: settings.id,
        [field]: tags,
      });
    };

  const handleConfirmDelete = () => {
    if (!deleteTagState || !settings?.id) return;

    const { field, tag } = deleteTagState;
    const currentTags = settings[field] ?? [];
    const updatedTags = currentTags.filter((t: string) => t !== tag);

    updateSettings({
      id: settings.id,
      [field]: updatedTags,
    });

    setDeleteTagState(null);
  };

  if (!settings) {
    return (
      <Card className="border-none">
        <CardContent className="p-6">Loading...</CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-none">
        <CardHeader>
          <CardTitle>Roles & Categories</CardTitle>
          <CardDescription>
            Manage employee roles and service categories for your business. Type then press enter to
            add.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2">Employee Roles</h3>
              <TagInput
                value={settings.employeeRole ?? []}
                onChange={handleTagsChange('employeeRole')}
                placeholder="Add employee role..."
                readOnly={['Other', 'Owner', 'Manager', 'Front Desk', 'Employee']}
                onDeleteTag={(tag) => setDeleteTagState({ field: 'employeeRole', tag })}
              />
            </div>
            <Separator />
            <div>
              <h3 className="text-sm font-medium mb-2">Service Categories</h3>
              <TagInput
                value={settings.serviceCategory ?? []}
                onChange={handleTagsChange('serviceCategory')}
                placeholder="Add service category..."
                onDeleteTag={(tag) => setDeleteTagState({ field: 'serviceCategory', tag })}
              />
            </div>
            <Separator />
            <div>
              <h3 className="text-sm font-medium mb-2">Special Categories</h3>
              <TagInput
                value={settings.specialCategory ?? []}
                onChange={handleTagsChange('specialCategory')}
                placeholder="Add special category..."
                onDeleteTag={(tag) => setDeleteTagState({ field: 'specialCategory', tag })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteTagState !== null}
        onOpenChange={(open) => !open && setDeleteTagState(null)}
        title="Delete Tag"
        description={`Are you sure you want to delete "${deleteTagState?.tag}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="destructive"
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default RoleAndCategoryTab;
